import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useGetGames } from '../../../hooks/queries/games/games/use-get-games'
import type { Game } from '../../../services/games/games/types'

function postMessage(message: string) {
  if ('ReactNativeWebView' in window) {
    // @ts-ignore
    window.ReactNativeWebView.postMessage(message)
  }
}

export const ProfileGames = memo(() => {
  const [selectedGame, setSelectedGame] = useState<Game | null>(null)
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false)
  const searchParams = useMemo(() => {
    return new URLSearchParams(window.location.search)
  }, [])

  const token = useMemo(() => {
    return searchParams.get('token') ?? localStorage.getItem('@token')
  }, [searchParams])

  const hideGameBackButton = useMemo(() => {
    return searchParams.get('hideGameBackButton') === '1'
  }, [searchParams])

  const mainContainerHeight = useMemo(() => {
    // jogo sempre em tela cheia; esconder o botão voltar não deve encolher pra 50vh
    return '100vh'
  }, [hideGameBackButton])

  const { games, isLoading } = useGetGames(
    {
      active: true,
      deleted: false,
      take: 100,
      skip: 0,
    },
    { enabled: Boolean(token), token },
  )

  const selectedGameUrl = useMemo(() => {
    if (!selectedGame?.url || !token) {
      return null
    }

    const separator = selectedGame.url.includes('?') ? '&' : '?'
    const hideBackButtonParam = hideGameBackButton ? '&hideBackButton=1' : ''
    const slotGameIdParam =
      selectedGame.slotGameId != null ? `&slotGameId=${selectedGame.slotGameId}` : ''

    return `${selectedGame.url}${separator}token=${token}${hideBackButtonParam}${slotGameIdParam}`
  }, [hideGameBackButton, selectedGame, token])

  const handleSelectGame = useCallback((game: Game) => {
    setSelectedGame(game)
  }, [])

  const handleCloseGame = useCallback(() => {
    setIsCloseConfirmOpen(false)
    setSelectedGame(null)
  }, [])

  const handleCancelClose = useCallback(() => {
    setIsCloseConfirmOpen(false)
  }, [])

  const handleBack = useCallback(() => {
    if (!selectedGame) {
      postMessage('close')
      return
    }

    setIsCloseConfirmOpen(true)
  }, [selectedGame])

  useEffect(() => {
    const handleNativeBack = (event: MessageEvent<string>) => {
      // 'native-back' vem do RN; 'close-game' vem do icone de sair dentro do jogo (iframe)
      if (event.data === 'native-back' || event.data === 'close-game') {
        handleBack()
        return
      }

      // 'open-modal-recharge' vem do botao de recarga dentro do jogo (iframe);
      // repassamos pro RN abrir a tela de recarga de moedas.
      if (event.data === 'open-modal-recharge') {
        postMessage('open-modal-recharge')
      }
    }

    window.addEventListener('message', handleNativeBack)
    document.addEventListener('message', handleNativeBack as EventListener)

    return () => {
      window.removeEventListener('message', handleNativeBack)
      document.removeEventListener('message', handleNativeBack as EventListener)
    }
  }, [handleBack])

  // só jogos ativos aparecem na tela
  const activeGames = useMemo(() => {
    return games?.filter((game) => game.active) ?? []
  }, [games])

  const slotGames = useMemo(() => {
    return activeGames.filter(
      (game) => game.gameSection?.trim().toLowerCase() === 'slots',
    )
  }, [activeGames])

  const spinWheelGames = useMemo(() => {
    return activeGames.filter((game) => {
      const section = game.gameSection?.trim().toLowerCase()

      return section === 'spin-wheel' || section === 'spin wheel'
    })
  }, [activeGames])

  const otherGames = useMemo(() => {
    return activeGames.filter((game) => {
      const section = game.gameSection?.trim().toLowerCase()

      return (
        section !== 'slots' &&
        section !== 'spin-wheel' &&
        section !== 'spin wheel'
      )
    })
  }, [activeGames])

  return (
    <>
      {selectedGameUrl && (
        <div className="pointer-events-none fixed inset-0 z-30 flex items-end justify-center bg-transparent">
          <iframe
            src={selectedGameUrl}
            title={selectedGame?.gameKey ?? 'profile-game'}
            className="pointer-events-auto w-full border-0 bg-transparent"
            allow="fullscreen"
            allowTransparency={true}
            style={{
              backgroundColor: 'transparent',
              height: selectedGame?.height || mainContainerHeight,
            }}
          />

          {isCloseConfirmOpen && (
            <div
              className="pointer-events-auto absolute inset-0 z-20 flex items-center justify-center bg-black/70 px-6 backdrop-blur-sm"
              onClick={handleCancelClose}
            >
              <div
                role="dialog"
                aria-modal="true"
                onClick={(event) => event.stopPropagation()}
                className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#11162a] p-6 text-center shadow-2xl"
              >
                <h2 className="text-lg font-black text-white">
                  Fechar o jogo?
                </h2>

                <p className="mt-2 text-sm text-white/60">
                  Seu progresso atual da rodada pode ser perdido.
                </p>

                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={handleCancelClose}
                    className="flex-1 rounded-full border border-white/20 bg-white/5 px-4 py-3 text-sm font-bold text-white"
                  >
                    Cancelar
                  </button>

                  <button
                    type="button"
                    onClick={handleCloseGame}
                    className="flex-1 rounded-full bg-orange-500 px-4 py-3 text-sm font-bold text-white"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div
        className="w-screen bg-[#0b1020] text-white"
        style={{ minHeight: mainContainerHeight }}
      >
      <div
        className="mx-auto flex w-full max-w-5xl flex-col px-4 py-6"
        style={{ minHeight: mainContainerHeight }}
      >
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-black tracking-tight">Games</h1>

          <button
            type="button"
            onClick={handleBack}
            className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold"
          >
            Fechar
          </button>
        </div>

        <section className="mb-8">
          <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.24em] text-white/60">
            Slots
          </h2>

          {isLoading && (
            <span className="text-sm text-white/60">Carregando...</span>
          )}

          <div className="flex flex-wrap gap-4">
            {slotGames.map((game) => (
              <button
                key={game.id}
                type="button"
                onClick={() => handleSelectGame(game)}
                className="aspect-square flex-[1_1_calc(25%-0.75rem)] max-w-[calc(25%-0.75rem)] overflow-hidden rounded-2xl border border-white/10 bg-white/5"
              >
                <img
                  src={game.imgLogo}
                  alt={game.gameLabel}
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </section>

        <section>
          <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.24em] text-white/60">
            Spin Wheel
          </h2>

          <div className="flex flex-wrap gap-4">
            {spinWheelGames.map((game) => (
              <button
                key={game.id}
                type="button"
                onClick={() => handleSelectGame(game)}
                className="aspect-square flex-[1_1_calc(25%-0.75rem)] max-w-[calc(25%-0.75rem)] overflow-hidden rounded-2xl border border-white/10 bg-white/5"
              >
                <img
                  src={game.imgLogo}
                  alt={game.gameLabel}
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </section>

        <section className="mt-8">
          <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.24em] text-white/60">
            Others
          </h2>

          <div className="flex flex-wrap gap-4">
            {otherGames.map((game) => (
              <button
                key={game.id}
                type="button"
                onClick={() => handleSelectGame(game)}
                className="aspect-square flex-[1_1_calc(25%-0.75rem)] max-w-[calc(25%-0.75rem)] overflow-hidden rounded-2xl border border-white/10 bg-white/5"
              >
                <img
                  src={game.imgLogo}
                  alt={game.gameLabel}
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </section>
      </div>
      </div>
    </>
  )
})

ProfileGames.displayName = 'ProfileGames'
